import { useCallback, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAPI } from '../../Contexts/APIContext'
import { resolveIngestInput } from '../../Helpers/ingestUrlResolver'
import { buildCreateBody, newClientKey, CHANNEL_ID_RE } from '../../Pages/IngestRequests/requestContract'

/**
 * Starting an artist sync from anywhere in FAST. Resolves whatever was pasted
 * to a channel id before sending, and refuses anything that will not reduce
 * to one. No duplicate check - the service joins an in-progress request itself.
 */

export const REQUEST_STATE = Object.freeze({
  IDLE: 'idle',
  SENDING: 'sending',
  SENT: 'sent',
  REFUSED: 'refused',
  FAILED: 'failed',
})

/**
 * A channel id, or a named reason there isn't one. An @handle or a FAST link
 * is a fine identifier - just not one this endpoint takes.
 */
export function channelIdFrom(input) {
  const raw = typeof input === 'string' ? input.trim() : ''
  if (!raw) return { ok: false, reason: 'empty' }
  if (CHANNEL_ID_RE.test(raw)) return { ok: true, channelId: raw }
  // A half-pasted channel id is not a name. Without this the resolver reads
  // "UCy3zgWom-5AGypGX_FVTKp" as prose, because it has capitals and no
  // punctuation, and tells the person it looks like a name.
  if (/^UC[\w-]*$/.test(raw)) return { ok: false, reason: 'partial_channel_id' }

  const resolved = resolveIngestInput(raw)
  if (!resolved.ok) return { ok: false, reason: resolved.reason }
  if (resolved.target.type !== 'artist') return { ok: false, reason: 'not_an_artist' }
  if (resolved.target.idType !== 'channelId') return { ok: false, reason: 'needs_channel_id' }
  return { ok: true, channelId: resolved.target.idValue }
}

export default function useIngestRequest() {
  const { startIngestRequest } = useAPI()
  const queryClient = useQueryClient()

  const [state, setState] = useState(REQUEST_STATE.IDLE)
  const [message, setMessage] = useState(null)


  // Held across retries of the SAME attempt so a retry cannot create a second
  // request. Re-minted whenever the input changes, because the server rejects
  // a key replayed with a different source or force_refresh (store.js).
  const attempt = useRef(null)
  const inFlight = useRef(false)
  // Bumped by reset() so a submit abandoned mid-flight cannot report back.
  const generation = useRef(0)

  const reset = useCallback(() => {
    attempt.current = null
    // inFlight is deliberately NOT cleared: the request is still in the air,
    // and clearing it here let a second press mint a new key and file again.
    generation.current += 1
    setState(REQUEST_STATE.IDLE)
    setMessage(null)
  }, [])

  /**
   * @param input what the person supplied - a channel id, or a link with one in it
   * @param opts.forceRefresh skip the 24-hour reuse window and fetch again
   */
  const submit = useCallback(
    async (input, opts = {}) => {
      if (inFlight.current) return { ok: false, reason: 'in_flight' }

      const found = channelIdFrom(input)
      if (!found.ok) {
        setState(REQUEST_STATE.REFUSED)
        setMessage(found.reason)
        return { ok: false, reason: found.reason }
      }
      // Clear a finished attempt so a new ask mints a new key rather than
      // replaying the old one onto a completed request.
      if (state === REQUEST_STATE.SENT) attempt.current = null

      const force = Boolean(opts.forceRefresh)
      const current = attempt.current
      if (!current || current.source !== found.channelId || current.force !== force) {
        attempt.current = { key: newClientKey(), source: found.channelId, force }
      }
      const mine = generation.current
      const stillMine = () => generation.current === mine
      inFlight.current = true
      setMessage(null)
      setState(REQUEST_STATE.SENDING)

      try {
        const response = await startIngestRequest(
          buildCreateBody({ source: found.channelId, clientKey: attempt.current.key, forceRefresh: force })
        )
        const request = (response && (response.request || response)) || {}
        inFlight.current = false
        queryClient.invalidateQueries({ queryKey: ['ingest-requests'] })
        // `reused` is the server's word (store.js): the artist was already
        // being synchronized, or this client_key was a replay, so it joined
        // the existing request rather than starting a second.
        const reused = Boolean(response && response.reused)
        if (!stillMine()) return { ok: true, id: request.id || null, reused, stale: true }
        setState(REQUEST_STATE.SENT)
        return { ok: true, id: request.id || null, reused }
      } catch (err) {
        inFlight.current = false
        if (!stillMine()) return { ok: false, reason: 'api_error', stale: true }
        const failure = (err && err.message) || 'We could not start that.'
        setMessage(failure)
        setState(REQUEST_STATE.FAILED)
        return { ok: false, reason: 'api_error', message: failure }
      }
    },
    [startIngestRequest, queryClient]
  )

  return { state, message, submit, reset }
}
